import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "../../../components/Logo";
import ProgressSteps from "../../../components/ProgressSteps";
import CountryPhoneInput from "../../../components/CountryPhoneInput";
import { getTypes, type TypeOption } from "../../../api/authEtablissement";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

const SIGNUP_STORAGE_KEY = "maps.signup";

/** Bénin (+229) garde son format historique "01" + 8 chiffres ; les autres pays acceptent 6 à 12 chiffres. */
function isNationalNumberValid(dialCode: string, national: string): boolean {
  if (dialCode === "229") return /^01\d{8}$/.test(national);
  return national.length >= 6 && national.length <= 12;
}

/** Groupe l'affichage par 2 chiffres ("01 23 45 67 89") pour faciliter la lecture. */
function groupByTwo(digits: string): string {
  return digits.replace(/(\d{2})(?=\d)/g, "$1 ");
}

export default function EtablissementSignupInfo() {
  const navigate = useNavigate();
  const [nom, setNom] = useState("");
  const [agre, setAgre] = useState("");
  const [type, setType] = useState("");
  const [adresse, setAdresse] = useState("");
  const [dialCode, setDialCode] = useState("229");
  const [nationalPhone, setNationalPhone] = useState("");
  const [directeurNom, setDirecteurNom] = useState("");
  const [directeurPrenom, setDirecteurPrenom] = useState("");
  const [types, setTypes] = useState<TypeOption[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  const phoneValid = useMemo(() => isNationalNumberValid(dialCode, nationalPhone), [dialCode, nationalPhone]);
  const telephone = useMemo(
    () => (nationalPhone ? `+${dialCode} ${groupByTwo(nationalPhone)}`.trim() : ""),
    [dialCode, nationalPhone]
  );

  useEffect(() => {
    getTypes()
      .then((result) => {
        if (result.ok && result.data?.length) setTypes(result.data);
      })
      .finally(() => setLoadingTypes(false));
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(SIGNUP_STORAGE_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw) as Record<string, unknown>;
      if (draft.mode !== "creation") return;
      if (typeof draft.nom === "string") setNom(draft.nom);
      if (typeof draft.numero_agrement === "string") setAgre(draft.numero_agrement);
      if (typeof draft.type === "string") setType(draft.type);
      if (typeof draft.adresse === "string") setAdresse(draft.adresse);
      if (typeof draft.telephone === "string") {
        const match = /^\+(\d+)\s*(.*)$/.exec(draft.telephone);
        if (match) {
          setDialCode(match[1]);
          setNationalPhone(match[2].replace(/\D/g, ""));
        }
      }
      if (typeof draft.directeur_nom === "string") setDirecteurNom(draft.directeur_nom);
      if (typeof draft.directeur_prenom === "string") setDirecteurPrenom(draft.directeur_prenom);
    } catch {
      /* ignore */
    }
  }, []);

  const canProceed = nom !== "" && type !== "" && adresse !== "";

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canProceed) return;
    try {
      localStorage.setItem(
        SIGNUP_STORAGE_KEY,
        JSON.stringify({
          mode: "creation",
          nom,
          numero_agrement: agre || undefined,
          type,
          adresse,
          telephone: telephone || undefined,
          directeur_nom: directeurNom || undefined,
          directeur_prenom: directeurPrenom || undefined,
        })
      );
    } catch {
      /* ignore */
    }
    navigate("/etablissement/inscription/identifiants");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 relative">
      {/* Mobile background image */}
      <div className="absolute inset-0 md:hidden">
        <img src={'/maps-login-side.jpg'} alt="signup-info" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-white/30" />
      </div>

      {/* Left: form */}
      <div className="relative flex items-center justify-center px-4 md:px-10 py-10">
        <div className="w-full max-w-md">
          <div className="flex justify-center"><Logo size={56} /></div>
          <div className="mt-6 text-sm" style={{ color: "#00553E" }}>
            <Link to="/etablissement/inscription/recherche" className="inline-flex items-center gap-2">
              <ArrowLeft size={16} /> Retour
            </Link>
          </div>
          <h1 className="mt-2 text-2xl md:text-3xl font-semibold text-[#0B1F16]">Créer le compte de l'établissement</h1>
          <div className="mt-1 text-gray-500 font-semibold">Informations</div>

          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            <div className="space-y-2">
              <label className="text-sm">Nom de l'établissement</label>
              <input
                placeholder="SAINT JEAN"
                value={nom}
                onChange={(event) => setNom(event.target.value.toUpperCase())}
                className="w-full h-12 rounded-[10px] border px-3 bg-white"
                style={{ borderColor: "#E5E7EB" }}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm">Type de structure</label>
              <Select value={type} onValueChange={setType} disabled={loadingTypes}>
                <SelectTrigger
                  className="w-full h-12 rounded-[10px] border px-3 bg-white"
                  style={{ borderColor: "#E5E7EB" }}
                >
                  <SelectValue placeholder="Choisir…" />
                </SelectTrigger>
                <SelectContent>
                  {(types.length > 0
                    ? types.map((t) => ({
                        value: t.type ?? t.code ?? t.libelle ?? t.label ?? "",
                        label: t.libelle ?? t.label ?? t.code ?? t.id ?? "",
                      }))
                    : [
                        { value: "Laboratoire", label: "Laboratoire" },
                        { value: "Hôpital", label: "Hôpital" },
                        { value: "Clinique", label: "Clinique" },
                        { value: "Cabinet", label: "Cabinet" },
                      ]
                  ).map(({ value, label }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm">Adresse</label>
              <input
                placeholder="Quartier, rue, ville…"
                value={adresse}
                onChange={(event) => setAdresse(event.target.value.toUpperCase())}
                className="w-full h-12 rounded-[10px] border px-3 bg-white"
                style={{ borderColor: "#E5E7EB" }}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm">Téléphone <span className="text-gray-400">(optionnel)</span></label>
              <CountryPhoneInput
                dialCode={dialCode}
                national={nationalPhone}
                onDialCodeChange={setDialCode}
                onNationalChange={setNationalPhone}
                invalid={nationalPhone.length > 0 && !phoneValid}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm">N° d'identification / Agrément <span className="text-gray-400">(optionnel)</span></label>
              <input
                placeholder="9707ZZ608E62E8"
                value={agre}
                onChange={(event) => setAgre(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                className="w-full h-12 rounded-[10px] border px-3 bg-white"
                style={{ borderColor: "#E5E7EB" }}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm">Nom du directeur <span className="text-gray-400">(optionnel)</span></label>
              <input
                value={directeurNom}
                onChange={(event) => setDirecteurNom(event.target.value.toUpperCase())}
                className="w-full h-12 rounded-[10px] border px-3 bg-white"
                style={{ borderColor: "#E5E7EB" }}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm">Prénom du directeur <span className="text-gray-400">(optionnel)</span></label>
              <input
                value={directeurPrenom}
                onChange={(event) => setDirecteurPrenom(event.target.value.toUpperCase())}
                className="w-full h-12 rounded-[10px] border px-3 bg-white"
                style={{ borderColor: "#E5E7EB" }}
              />
            </div>

            <button
              type="submit"
              className="w-full h-12 rounded-[10px] font-semibold cursor-pointer disabled:opacity-60"
              style={{ backgroundColor: canProceed ? "#00553E" : "#DCEFE9", color: canProceed ? "#FFFFFF" : "#7aa79a" }}
              disabled={!canProceed}
            >
              Suivant
            </button>
          </form>

          <div className="mt-8">
            <ProgressSteps step={2} total={5} />
          </div>
        </div>
      </div>

      {/* Right: image (desktop) */}
      <div className="relative hidden md:block">
        <img src={'/maps-login-side.jpg'} alt="signup-info" className="absolute inset-0 w-full h-full object-cover" />
      </div>
    </div>
  );
}
